import { useState } from 'react'
import { ChevronDown } from 'lucide-react'

const faqs = [
    {
        question: 'What exactly is a cliché, and why should I avoid them?',
        answer: 'A cliché is an overused phrase or expression that has lost its original impact through repetition. Examples include "think outside the box" or "at the end of the day." Avoiding clichés makes your writing more original, engaging, and professional. It shows readers you\'ve put genuine thought into your words.',
    },
    {
        question: 'How does InkSpot\'s AI detection work?',
        answer: 'InkSpot uses a combination of pattern matching and advanced AI (powered by Google\'s Gemini) to analyze your text. Our system maintains a database of 2,000+ known clichés and uses contextual AI to identify subtle overused patterns. The AI also provides intelligent alternatives that match your writing style and tone.',
    },
    {
        question: 'Does InkSpot work with my favorite writing tools?',
        answer: 'Yes! Our Chrome extension works seamlessly with Gmail, Google Docs, Twitter/X, LinkedIn, Medium, WordPress, and most text editors in your browser. The web app works standalone for drafting and editing. We\'re constantly adding support for more platforms.',
    },
    {
        question: 'Is my writing data private and secure?',
        answer: 'Absolutely. Your text is processed in real-time and is never stored on our servers for pattern matching. When using AI features, data is transmitted securely and deleted immediately after processing. We do not use your content to train models or share it with third parties.',
    },
    {
        question: 'What\'s the difference between Free and Pro?',
        answer: 'The Free plan offers 10 AI-powered detections per day with basic pattern matching. Pro unlocks unlimited AI detections, advanced suggestions, custom style guides, the full analytics dashboard, and priority support. Pro is perfect for professional writers, content teams, and anyone serious about improving their writing.',
    },
    {
        question: 'Can I cancel my Pro subscription anytime?',
        answer: 'Yes! You can cancel your subscription at any time from your account settings. Your Pro features will remain active until the end of your current billing period. We also offer a 30-day money-back guarantee if you\'re not satisfied.',
    },
    {
        question: 'Do you offer team or enterprise plans?',
        answer: 'We\'re working on team plans with shared style guides, centralized billing, and admin controls. If you\'re interested in an enterprise solution, please contact us at support@inkspot.icu and we\'ll work with you on a custom plan.',
    },
    {
        question: 'How accurate is the cliché detection?',
        answer: 'Our detection accuracy is over 95% for common clichés and improves continuously as we refine our AI models. You can also report false positives or suggest new patterns, helping us improve for everyone. Pro users can customize sensitivity levels to match their preferences.',
    },
]

function FAQItem({ question, answer, isOpen, onClick }) {
    return (
        <div
            className={`glass-card overflow-hidden transition-all duration-300 
                  ${isOpen ? 'border-primary-400/50' : 'hover:border-white/20'}`}
        >
            <button
                onClick={onClick}
                className="w-full flex items-center justify-between p-6 text-left"
            >
                <span className="text-lg font-medium text-white pr-4">{question}</span>
                <ChevronDown
                    className={`w-5 h-5 text-primary-400 flex-shrink-0 transition-transform duration-300 
                     ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>
            <div
                className={`overflow-hidden transition-all duration-300 
                   ${isOpen ? 'max-h-96' : 'max-h-0'}`}
            >
                <p className="px-6 pb-6 text-white/60 leading-relaxed">
                    {answer}
                </p>
            </div>
        </div>
    )
}

export default function FAQ() {
    const [openIndex, setOpenIndex] = useState(0)

    return (
        <section id="faq" className="relative py-24 sm:py-32 overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-slate-950" />
            <div className="absolute inset-0 bg-gradient-radial from-accent-950/30 via-transparent to-transparent" />

            {/* Content */}
            <div className="relative z-10 section-container">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="section-title">
                        Frequently Asked{' '}
                        <span className="gradient-text">Questions</span>
                    </h2>
                    <p className="section-subtitle">
                        Everything you need to know about InkSpot.
                        Can't find an answer? Reach out to our support team.
                    </p>
                </div>

                {/* FAQ List */}
                <div className="max-w-3xl mx-auto space-y-4">
                    {faqs.map((faq, index) => (
                        <FAQItem
                            key={index}
                            question={faq.question}
                            answer={faq.answer}
                            isOpen={openIndex === index}
                            onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                        />
                    ))}
                </div>

                {/* Contact CTA */}
                <div className="mt-12 text-center">
                    <p className="text-white/50 mb-4">Still have questions?</p>
                    <a
                        href="mailto:support@inkspot.icu"
                        className="btn-secondary inline-flex"
                    >
                        Contact Support
                    </a>
                </div>
            </div>
        </section>
    )
}
